// Nudge mode: the cursor as a hand, pushing and pulling what is in the scene.
//
// Pressed, the button arms the canvas; a drag then picks whatever link or object
// is under the pointer and holds a force on it, at the point it was grabbed,
// until the pointer is let go. Pressed again it disarms and the canvas goes back
// to orbiting.
//
// **The simulation keeps running**, which is the whole difference between this
// and the placement gizmo in `place.ts`. That one freezes the scene and writes
// poses: it is answering "where does this object belong", and the answer has to
// be exact. This writes a *force* and nothing else, so what the robot does with
// it is the solve's answer -- a link held by its servos barely moves, a loose
// one swings, and a thing on the ground slides and topples the way its mass
// says it should.
//
// Which is also why it is called nudging rather than grabbing or dragging. The
// link does not go where the cursor goes: there is a cap on how hard the hand
// can push, and it starts at a hand laid on a robot arm rather than at a crane.
// The user moves it -- how hard a hand is depends on what it is laid on, and a
// figure in Newtons means nothing without something to read it against, so the
// box sits under a short list of what a fingertip, a lift and a shove are worth.
//
// The force is a damped spring from the point that was grabbed to the point
// under the cursor, and both halves matter. Without the spring an arm cannot be
// led anywhere, since a constant push keeps accelerating it; without the damping
// the same arm rings, because a stiff spring on a light link at this timestep is
// an oscillator with nothing to lose energy to.
//
// **The spring is the engine's to evaluate, not this module's.** All that goes
// across is where the hand took hold and where it is pulling to; the force comes
// out of the solve, every step. This module runs on animation frames and the
// physics steps sixty times between two of them, so a force worked out here is
// an answer to where the body was and how fast it was going sixty steps ago --
// which on a light body free to turn adds energy rather than removing it. A
// 20 g part grabbed by a corner used to spin up to a NaN within a second of
// being touched that way (roadmap#10).

import { InterfaceHybrid } from "simulator";
import { Simulator } from "gorilla-physics-ui";
import { Plane, Vector3 } from "three";
import { aim, pick } from "./pick";

// How hard the hand pulls per metre the grabbed point lags behind the cursor,
// and what that is capped at. **The cap is the user's** -- `#nudge-force-input`
// under the toggle -- and this is where it starts: a firm hand, enough to lead
// an arm its own servos are holding and to push a printed part over, not enough
// to throw either.
//
// The two are read together, and the quotient is what the drag actually feels
// like: the cap says how hard the hand can ever push, and `DEFAULT_FORCE /
// STIFFNESS` says how far the cursor has to run ahead of the grabbed point
// before it is pushing that hard -- 4 cm, roughly where a real hand's give runs
// out. Raising the cap without raising the stiffness to match stretches every
// force below it over a longer drag, which reads as a sluggish hand rather than
// a strong one. So this pair is a *ratio* rather than two figures, and a force
// the user types scales the stiffness with it: the hand saturates at the same
// lag at 2 N as at 200.
const DEFAULT_FORCE = 20; // N
const STIFFNESS = 500; // N/m, at DEFAULT_FORCE
// Near critical for the tens of grams a printed link weighs. Too little and a
// grabbed link rings around the cursor; too much and it will not follow at all.
// The engine holds it below what the body it lands on can absorb in a step, so
// this is a ceiling rather than the figure a light part actually gets.
//
// It scales as the *square root* of the force, not with it: critical damping is
// `2 sqrt(k m)`, so a stiffness raised tenfold wants a damping raised by about
// three, and a fixed 8 N s/m on the stiffer spring is what rings.
const DAMPING = 8; // N s/m, at DEFAULT_FORCE

// What the next grab will be capped at. Kept here rather than read off the box
// at the grab, so an entry that is empty or out of the bounds the markup sets
// leaves the last good value standing instead of nudging with nothing.
let force = DEFAULT_FORCE;

// What is being pushed, while a drag is in progress.
interface Grab {
  articulated: number;
  // The plane the cursor is read against: through the grab point, facing the
  // camera. A pointer gives two numbers and a force needs three, and this is
  // the third that does not surprise anyone.
  plane: Plane;
}

let simulator: Simulator | undefined;
let hybrid: InterfaceHybrid | undefined;
let armed = false;
let allowed = true;
// What to switch off when this arms. The page wires it, rather than the two
// modes importing each other.
let disarmOthers: () => void = () => {};
let grab: Grab | undefined;

function element<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) {
    throw new Error(`index.html has no #${id}`);
  }
  return found as T;
}

function canvas(): HTMLCanvasElement | undefined {
  return simulator?.graphics.renderer.domElement;
}

// Arm the mode's toggle and put its listeners on the canvas. Called once, with
// the scene the whole page shares: a drop replaces what is in that scene but
// never the handle to it, and which body is being pushed is worked out at each
// grab rather than kept.
export function allowNudging(
  scene: Simulator,
  world: InterfaceHybrid,
  disarmOtherModes: () => void,
) {
  simulator = scene;
  hybrid = world;
  disarmOthers = disarmOtherModes;

  const toggle = element<HTMLButtonElement>("nudge-toggle");
  toggle.addEventListener("click", () => setArmed(!armed));

  const input = element<HTMLInputElement>("nudge-force-input");
  input.value = String(DEFAULT_FORCE);
  input.addEventListener("input", () => {
    if (input.value !== "" && input.checkValidity()) {
      force = input.valueAsNumber;
    }
  });

  const element_ = canvas();
  if (!element_) {
    return;
  }
  element_.addEventListener("pointerdown", onPointerDown);
  element_.addEventListener("pointermove", onPointerMove);
  element_.addEventListener("pointerup", onPointerUp);
  element_.addEventListener("pointercancel", onPointerUp);
}

// Let the page refuse the mode while something else owns the pointer -- placing
// a dropped object, which puts a gizmo on the canvas and freezes the physics
// this needs running. Refused, the mode is switched off rather than merely
// blocked, so the button never says it is on while it is not.
export function setNudgingAllowed(value: boolean) {
  allowed = value;
  if (!allowed) {
    setArmed(false);
  }
  element<HTMLButtonElement>("nudge-toggle").disabled = !allowed;
}

// Switch the mode off from outside -- `fix.ts` arming, which wants the same
// canvas. Kept separate from `setNudgingAllowed` because it is a different
// statement: the mode is still available, it is simply not the one in use.
export function disarmNudging() {
  setArmed(false);
}

function setArmed(value: boolean) {
  if (value && !allowed) {
    return;
  }
  // The two modes both answer a press on the canvas, and a press that did two
  // things at once would be neither.
  if (value) {
    disarmOthers();
  }
  armed = value;
  if (!armed) {
    release();
  }
  const toggle = element<HTMLButtonElement>("nudge-toggle");
  toggle.setAttribute("aria-pressed", String(armed));
  toggle.classList.toggle("held", armed);
  // The force goes up and down with the mode: it is only ever a number about a
  // drag, and there are no drags while the canvas is orbiting.
  element<HTMLDivElement>("nudge-force").hidden = !armed;
  const element_ = canvas();
  if (element_) {
    element_.style.cursor = armed ? "grab" : "";
  }
}

function onPointerDown(event: PointerEvent) {
  if (!armed || !simulator || !hybrid || grab || event.button !== 0) {
    return;
  }
  const owner = pick(simulator, hybrid, event);
  if (!owner) {
    return;
  }

  const point = owner.point;
  const facing = new Vector3();
  simulator.graphics.camera.getWorldDirection(facing);
  // The whole hand scales with the force the user asked for, rather than the
  // cap moving on its own: the stiffness goes with it, so the lag the hand
  // saturates at stays where it was, and the damping with its square root, so
  // the spring stays as close to critical on the same body.
  const scale = force / DEFAULT_FORCE;
  // Taking hold is the one call that names a point on the body. From here on
  // the engine tracks that material point itself and only the target moves.
  try {
    hybrid.set_applied_spring_at(
      owner.articulated,
      owner.body,
      point.x,
      point.y,
      point.z,
      point.x,
      point.y,
      point.z,
      STIFFNESS * scale,
      DAMPING * Math.sqrt(scale),
      force,
    );
  } catch (error) {
    console.error("nudge: could not take hold", error);
    return;
  }
  grab = {
    articulated: owner.articulated,
    plane: new Plane().setFromNormalAndCoplanarPoint(facing, point),
  };

  // Orbiting and this both start with a press on the same canvas, so only one
  // of them can have it. `place.ts` makes the same trade with its gizmo.
  simulator.graphics.controls.enabled = false;
  const element_ = canvas();
  if (element_) {
    element_.setPointerCapture(event.pointerId);
    element_.style.cursor = "grabbing";
  }
}

function onPointerMove(event: PointerEvent) {
  if (!grab || !hybrid || !simulator) {
    return;
  }
  // Behind the camera, or exactly edge-on: leave the target where it was
  // rather than sending the force somewhere the user did not point.
  const found = aim(simulator, event).intersectPlane(grab.plane, new Vector3());
  if (!found) {
    return;
  }
  try {
    hybrid.aim_applied_spring_at(grab.articulated, found.x, found.y, found.z);
  } catch (error) {
    console.error("nudge: could not aim the hand", error);
    release();
  }
}

function onPointerUp(event: PointerEvent) {
  if (!grab) {
    return;
  }
  const element_ = canvas();
  if (element_ && element_.hasPointerCapture(event.pointerId)) {
    element_.releasePointerCapture(event.pointerId);
  }
  release();
}

// Take the force off and give the canvas back. Safe to call with nothing held.
function release() {
  if (grab && hybrid) {
    try {
      hybrid.clear_applied_spring_at(grab.articulated);
    } catch (error) {
      // The articulated can have gone out from under us -- a robot dropped
      // while a link was held. Nothing to clear in that case, and nothing to
      // report either.
      console.warn("nudge: could not let go", error);
    }
  }
  grab = undefined;
  if (simulator) {
    simulator.graphics.controls.enabled = true;
  }
  const element_ = canvas();
  if (element_) {
    element_.style.cursor = armed ? "grab" : "";
  }
}
